'use client';

import React from 'react';
import { Search, X } from 'lucide-react';
import { GuitarStatus, GuitarType } from './mockData';

interface CollectionFiltersProps {
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: GuitarStatus | 'all';
  onStatusChange: (v: GuitarStatus | 'all') => void;
  typeFilter: GuitarType | 'all';
  onTypeChange: (v: GuitarType | 'all') => void;
  brandFilter: string;
  onBrandChange: (v: string) => void;
  uniqueBrands: string[];
}

const statusOptions: { value: GuitarStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'on-loan', label: 'On Loan' },
  { value: 'for-sale', label: 'For Sale' },
  { value: 'archived', label: 'Archived' },
];

const typeOptions: { value: GuitarType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'Electric', label: 'Electric' },
  { value: 'Acoustic', label: 'Acoustic' },
  { value: 'Bass', label: 'Bass' },
  { value: 'Classical', label: 'Classical' },
];

export default function CollectionFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusChange,
  typeFilter,
  onTypeChange,
  brandFilter,
  onBrandChange,
  uniqueBrands,
}: CollectionFiltersProps) {
  const hasActiveFilters = statusFilter !== 'all' || typeFilter !== 'all' || brandFilter !== 'all' || search.trim() !== '';

  function clearAll() {
    onSearchChange('');
    onStatusChange('all');
    onTypeChange('all');
    onBrandChange('all');
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
      {/* Search */}
      <div className="relative flex-1 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search brand, model, serial…"
          className="w-full h-9 pl-9 pr-3 rounded-lg border border-input bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors hover:border-primary/40"
          suppressHydrationWarning
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Status filter */}
      <select
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value as GuitarStatus | 'all')}
        className="h-9 px-3 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer hover:border-primary/40 transition-colors"
      >
        {statusOptions.map((o) => (
          <option key={`status-opt-${o.value}`} value={o.value}>{o.label}</option>
        ))}
      </select>

      {/* Type filter */}
      <select
        value={typeFilter}
        onChange={(e) => onTypeChange(e.target.value as GuitarType | 'all')}
        className="h-9 px-3 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer hover:border-primary/40 transition-colors"
      >
        {typeOptions.map((o) => (
          <option key={`type-opt-${o.value}`} value={o.value}>{o.label}</option>
        ))}
      </select>

      {/* Brand filter */}
      <select
        value={brandFilter}
        onChange={(e) => onBrandChange(e.target.value)}
        className="h-9 px-3 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer hover:border-primary/40 transition-colors"
      >
        <option value="all">All Brands</option>
        {uniqueBrands.map((b) => (
          <option key={`brand-opt-${b}`} value={b}>{b}</option>
        ))}
      </select>

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          onClick={clearAll}
          className="flex items-center gap-1.5 h-9 px-3 text-sm font-500 text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-all duration-150"
        >
          <X size={13} />
          Clear
        </button>
      )}
    </div>
  );
}